"use client";

import { Check, type LucideIcon } from "lucide-react";
import Image from "next/image";
import { type CSSProperties, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";

import styles from "./ui.module.css";

/**
 * Illustrative UI for product moments: small pieces drawn in the app's
 * editorial theme, and the motion helpers that play them. A moment wraps
 * itself in UiRoot so the tokens resolve against the page around it (the
 * accent is the page's; success is the app's green). The home's match band
 * and the playground's studies are drawn with it. True circles carry
 * rounded-full, so a square page (one-system) leaves them round.
 *
 * A panel is the product on the page, as a shot's window is, so it has a
 * shot's edge: the same 8px corners and the same line (ui.module.css). A
 * square page keeps a corner only on an element that asks for it, which is
 * what FRAME_CORNERS is for: Panel carries it, and so must anything else
 * drawn with `ui.panel` (a chip, the decision bar).
 */

export const ui = styles;

/** Marks an element whose own corners a square page keeps (one-system's data-frame-corners). */
export const FRAME_CORNERS = { "data-frame-corners": "" } as const;

export function UiRoot({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`${styles.ui} ${className}`}>{children}</div>;
}

export function Panel({
  children,
  className = "",
  lifted = false,
  style,
  anchor,
}: {
  children: ReactNode;
  className?: string;
  lifted?: boolean;
  style?: CSSProperties;
  anchor?: string;
}) {
  return (
    <div data-anchor={anchor} {...FRAME_CORNERS} className={`${styles.panel} ${lifted ? styles.lifted : ""} ${className}`} style={style}>
      {children}
    </div>
  );
}

/**
 * An entrance driven by state: rising out of a light blur, after `delay`.
 * Hidden, it resets at once (a study's stage has faded out by then).
 */
export function enter(shown: boolean, { delay = 0, y = 10, blur = 6, duration = 700 } = {}): CSSProperties {
  if (!shown) return { opacity: 0, transform: `translateY(${y}px)`, filter: `blur(${blur}px)`, transition: "none" };
  const timing = `${duration}ms var(--ui-ease) ${delay}ms`;
  return { opacity: 1, transform: "none", filter: "none", transition: `opacity ${timing}, transform ${timing}, filter ${timing}` };
}

/** A study's stage: it fades out on its last step so the loop restarts unseen. */
export function LoopStage({ out, children, className = "" }: { out: boolean; children: ReactNode; className?: string }) {
  return (
    <div className={`relative ${className}`} style={{ opacity: out ? 0 : 1, transition: "opacity 450ms var(--ui-ease)" }}>
      {children}
    </div>
  );
}

export type RunStatus = "waiting" | "running" | "completed";

/** The canvas's status marks: a dashed ring waiting, a spinner running, the success green's tick when done. */
export function StatusIcon({ status, size = 16 }: { status: RunStatus; size?: number }) {
  if (status === "completed") {
    return (
      <span className={`${styles.pop} flex shrink-0 items-center justify-center rounded-full bg-[var(--ui-success)] text-white`} style={{ width: size, height: size }}>
        <Check aria-hidden strokeWidth={3.2} style={{ width: size * 0.62, height: size * 0.62 }} />
      </span>
    );
  }
  if (status === "running") {
    return (
      <svg aria-hidden viewBox="0 0 16 16" width={size} height={size} className={`${styles.spin} shrink-0`}>
        <circle cx="8" cy="8" r="6.4" fill="none" stroke="var(--ui-line-strong)" strokeWidth="1.6" />
        <path d="M8 1.6a6.4 6.4 0 0 1 6.4 6.4" fill="none" stroke="var(--ui-primary)" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg aria-hidden viewBox="0 0 16 16" width={size} height={size} className="shrink-0">
      <circle cx="8" cy="8" r="6.4" fill="none" stroke="var(--ui-faint)" strokeWidth="1.3" strokeDasharray="2.1 2.3" />
    </svg>
  );
}

export const SOURCES = {
  xero: { name: "Xero", icon: "/concepts/source-icons/xero.svg" },
  hubspot: { name: "HubSpot", icon: "/concepts/source-icons/hubspot.svg" },
  outlook: { name: "Outlook", icon: "/concepts/source-icons/outlook.svg" },
} as const;

export type SourceId = keyof typeof SOURCES;

export function SourceMark({ source, size = 16 }: { source: SourceId; size?: number }) {
  return <Image src={SOURCES[source].icon} alt="" width={size} height={size} className="shrink-0 object-contain" />;
}

/** A source and the kind of record, in sentence case: “Xero contact”. */
export function SourceLabel({ source, kind }: { source: SourceId; kind: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[12px] text-[var(--ui-quiet)]">
      <SourceMark source={source} size={14} />
      <span>
        <span className="font-medium text-[var(--ui-ink)]">{SOURCES[source].name}</span> {kind}
      </span>
    </span>
  );
}

export function UiButton({
  tone = "outline",
  pressed = false,
  anchor,
  children,
}: {
  tone?: "outline" | "primary";
  pressed?: boolean;
  anchor?: string;
  children: ReactNode;
}) {
  return (
    <span data-anchor={anchor} className={`${styles.button} ${tone === "primary" ? styles.primary : styles.outline} ${pressed ? styles.pressed : ""}`}>
      {children}
    </span>
  );
}

/** A full pill, where the page keeps its corners; a square page squares it (it carries no rounded-full). For a study's own shapes: a panel, a chip and the decision bar take the panel's corners instead. */
export const PILL = { borderRadius: 999 } as const;

/** A value's mark: an accent line drawing under it once its evidence is in. */
export function Marked({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <span className="relative">
      {children}
      <span
        aria-hidden
        className="absolute inset-x-0 -bottom-[3px] h-[2px] origin-left rounded-full bg-[var(--ui-primary)]"
        style={{ transform: on ? "scaleX(1)" : "scaleX(0)", opacity: 0.7, transition: on ? "transform 600ms var(--ui-ease) 150ms" : "none" }}
      />
    </span>
  );
}

/**
 * A person's call on a proposal: the question and its three answers, then
 * what was chosen. Accept carries the `accept` anchor, for the pointer.
 * `after` adds to the outcome, such as the way back. Where the question and
 * the answers cannot share a line (a long question on a phone), the question
 * takes its own, above them.
 */
export function Decision({
  question,
  shown,
  pressed,
  made,
  after,
  className = "",
}: {
  question: string;
  shown: boolean;
  pressed: boolean;
  made: boolean;
  after?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex justify-center ${className}`} style={enter(shown, { y: 8 })}>
      <div {...FRAME_CORNERS} className={`${styles.panel} grid p-1.5`}>
        <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 [grid-area:1/1]" style={{ opacity: made ? 0 : 1, transition: "opacity 300ms" }}>
          <span className="pr-2 pl-3 text-[12.5px] whitespace-nowrap text-[var(--ui-quiet)]">{question}</span>
          <span className="flex items-center gap-1.5">
            <UiButton>Later</UiButton>
            <UiButton>Decline</UiButton>
            <UiButton tone="primary" anchor="accept" pressed={pressed}>
              Accept
            </UiButton>
          </span>
        </div>
        <div className="flex items-center justify-center gap-2 px-3 text-[12.5px] [grid-area:1/1]" style={{ opacity: made ? 1 : 0, transition: "opacity 400ms 200ms" }}>
          <Check aria-hidden strokeWidth={3} className="size-3.5 text-[var(--ui-success)]" />
          <span className="font-medium">Accepted</span>
          <span className="text-[var(--ui-quiet)]">by you, just now</span>
          {after}
        </div>
      </div>
    </div>
  );
}

export type Point = { x: number; y: number };

/**
 * The pointer: it glides to `at` (its tip lands there) and clicks while
 * `down`. Hidden, it fades where it is and moves without travelling, so a
 * study sends it back to its start only while it cannot be seen.
 */
export function Cursor({ at, down = false, shown }: { at: Point; down?: boolean; shown: boolean }) {
  return (
    <span
      aria-hidden
      className={styles.cursor}
      style={{
        transform: `translate(${at.x - 3}px, ${at.y - 2}px)`,
        opacity: shown ? 1 : 0,
        transition: shown ? "transform 1100ms var(--ui-ease), opacity 250ms" : "opacity 250ms",
      }}
    >
      <svg width="20" height="24" viewBox="0 0 20 24" className={`${styles.cursorBody} ${down ? styles.cursorDown : ""}`}>
        <path d="M3 2v17.2l4.4-4.1 2.9 6.6 3-1.3-2.9-6.5h6.2z" fill="#16171c" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
      {down ? <span className={`${styles.click} rounded-full`} /> : null}
    </span>
  );
}

export type Rect = { x: number; y: number; width: number; height: number };

/** An element's layout box relative to `root`, ignoring transforms (root must be positioned). */
export function layoutRect(node: HTMLElement, root: HTMLElement): Rect {
  let x = 0;
  let y = 0;
  let current: HTMLElement | null = node;
  while (current && current !== root) {
    x += current.offsetLeft;
    y += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return { x, y, width: node.offsetWidth, height: node.offsetHeight };
}

/**
 * The layout boxes of the elements marked `data-anchor` inside `root`, plus
 * `root` itself, re-measured whenever it resizes. Motion never moves them:
 * layout boxes ignore transforms.
 */
export function useAnchors(root: RefObject<HTMLElement | null>): Readonly<Record<string, Rect>> {
  const [rects, setRects] = useState<Record<string, Rect>>({});
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const measure = () => {
      const next: Record<string, Rect> = { root: { x: 0, y: 0, width: element.offsetWidth, height: element.offsetHeight } };
      for (const node of element.querySelectorAll<HTMLElement>("[data-anchor]")) {
        const name = node.dataset.anchor;
        if (name) next[name] = layoutRect(node, element);
      }
      setRects((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    };
    measure();
    void document.fonts?.ready.then(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [root]);
  return rects;
}

export const centre = (rect: Rect | undefined): Point => (rect ? { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 } : { x: 0, y: 0 });

/** A number counting up to `target` while `run`, easing out; at once with reduced motion. */
export function useTicker(target: number, run: boolean, duration = 900): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run) {
      setValue(0);
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      setValue(target * (1 - (1 - progress) ** 3));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, run, duration]);
  return value;
}

/** A packet of data travelling an SVG path drawn in the same box. */
export function Packet({ d, duration = 900, delay = 0 }: { d: string; duration?: number; delay?: number }) {
  return (
    <span
      aria-hidden
      className={`${styles.packet} rounded-full`}
      style={{ offsetPath: `path("${d}")`, "--travel": `${duration}ms`, "--delay": `${delay}ms` } as CSSProperties}
    />
  );
}

/** A segmented picker whose selection slides between its items, as the app's view picker does. */
export function Segmented({
  items,
  active,
  compactLabels = false,
}: {
  items: readonly { id: string; label: string; icon?: LucideIcon }[];
  active: string;
  /** Below `sm`, only the active item keeps its label. */
  compactLabels?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const measure = () => {
      const item = element.querySelector<HTMLElement>(`[data-item="${active}"]`);
      if (item) setBox({ left: item.offsetLeft, width: item.offsetWidth });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [active]);
  return (
    <div ref={root} className="relative flex w-fit rounded-[10px] bg-[color-mix(in_srgb,var(--ui-ink)_5%,transparent)] p-[3px]">
      {box ? (
        <span
          aria-hidden
          className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_0_0_1px_var(--ui-line),0_1px_3px_rgb(0_0_0/0.08)]"
          style={{ left: box.left, width: box.width, transition: "left 520ms var(--ui-spring), width 520ms var(--ui-spring)" }}
        />
      ) : null}
      {items.map((item) => {
        const on = item.id === active;
        return (
          <span
            key={item.id}
            data-item={item.id}
            className={`relative z-[1] flex items-center gap-1.5 rounded-[8px] px-2.5 py-[5px] text-[12.5px] transition-colors duration-300 ${
              on ? "font-medium text-[var(--ui-ink)]" : "text-[var(--ui-quiet)]"
            }`}
          >
            {item.icon ? <item.icon aria-hidden className="size-3.5" /> : null}
            <span className={compactLabels && !on ? "hidden sm:inline" : ""}>{item.label}</span>
          </span>
        );
      })}
    </div>
  );
}
